import React from 'react';
import { Table, Tag, Button, Space, Tooltip, Avatar } from 'antd';
import {
  EyeOutlined,
  StopOutlined,
  CaretRightOutlined,
  LoginOutlined,
  BankOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Tenant, SUBSCRIPTION_STATUS_COLORS, PLAN_SLUG_COLORS } from '../../types/tenant';

interface AdminTenantListProps {
  tenants: Tenant[];
  loading: boolean;
  total: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onViewDetails: (tenant: Tenant) => void;
  onSuspend: (tenant: Tenant) => void;
  onResume: (tenant: Tenant) => void;
  onImpersonate: (tenant: Tenant) => void;
}

const AdminTenantList: React.FC<AdminTenantListProps> = ({
  tenants,
  loading,
  total,
  currentPage,
  pageSize,
  onPageChange,
  onViewDetails,
  onSuspend,
  onResume,
  onImpersonate,
}) => {
  const columns: ColumnsType<Tenant> = [
    {
      title: 'Company',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (name: string, record: Tenant) => (
        <Space>
          <Avatar size="small" icon={<BankOutlined />} style={{ backgroundColor: '#f0f0f0', color: '#999' }} />
          <div>
            <div style={{ fontWeight: 500 }}>{name}</div>
            <div style={{ fontSize: 12, color: '#999' }}>{record.contactEmail}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Plan',
      dataIndex: 'planSlug',
      key: 'plan',
      width: 120,
      render: (slug: string, record: Tenant) => (
        <Tag color={PLAN_SLUG_COLORS[slug] || 'default'}>{record.planName || slug}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'subscriptionStatus',
      key: 'status',
      width: 160,
      render: (status: string, record: Tenant) => (
        <Space>
          <Tag color={SUBSCRIPTION_STATUS_COLORS[status] || 'default'}>{status}</Tag>
          {status === 'trial' && record.trialEndsAt && (
            <span style={{ fontSize: 12, color: '#999' }}>
              ends {new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(record.trialEndsAt))}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: 'Employees',
      dataIndex: 'employeeCount',
      key: 'employees',
      width: 100,
      align: 'right',
    },
    {
      title: 'Trucks',
      dataIndex: 'truckCount',
      key: 'trucks',
      width: 80,
      align: 'right',
    },
    {
      title: 'Revenue',
      dataIndex: 'basePrice',
      key: 'revenue',
      width: 120,
      align: 'right',
      render: (price: number) => <span style={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>${(price ?? 0).toLocaleString()}/mo</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      align: 'right',
      render: (_: unknown, record: Tenant) => (
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="View details">
            <Button type="text" size="small" aria-label="View details" icon={<EyeOutlined />} onClick={() => onViewDetails(record)} />
          </Tooltip>
          {(record.subscriptionStatus === 'active' || record.subscriptionStatus === 'trial') ? (
            <Tooltip title="Suspend">
              <Button type="text" size="small" danger aria-label="Suspend tenant" icon={<StopOutlined />} onClick={() => onSuspend(record)} />
            </Tooltip>
          ) : record.subscriptionStatus === 'suspended' ? (
            <Tooltip title="Resume">
              <Button type="text" size="small" aria-label="Resume tenant" icon={<CaretRightOutlined style={{ color: '#2E7D32' }} />} onClick={() => onResume(record)} />
            </Tooltip>
          ) : null}
          <Tooltip title="Login as tenant">
            <Button type="text" size="small" aria-label="Login as tenant" icon={<LoginOutlined style={{ color: '#1976D2' }} />} onClick={() => onImpersonate(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Table<Tenant>
      columns={columns}
      dataSource={tenants}
      rowKey="id"
      loading={loading}
      pagination={{
        total,
        current: currentPage,
        pageSize,
        showSizeChanger: false,
        showTotal: (t) => `${t} tenants`,
        onChange: (page) => onPageChange(page),
      }}
      onRow={(record) => ({
        onClick: () => onViewDetails(record),
        style: { cursor: 'pointer' },
      })}
      size="middle"
    />
  );
};

export default AdminTenantList;
